// Fila de parámetro (`.param-row` del mock): nombre/descripción | control + unidad | última
// modificación. Sin permiso de edición o sin llave en el servidor, el control queda deshabilitado.
import { Input, NumberInput, Switch } from '@/kit';
import { cn } from '@/lib/cn';
import { PARAM_UNIT, type ParamValue, type SystemParam } from './types';

export function ParamRow({
    param,
    value,
    dirty,
    readOnly,
    notConnected,
    onChange,
}: {
    param: SystemParam;
    /** Valor vigente en pantalla (guardado o en edición). */
    value: ParamValue;
    dirty: boolean;
    /** Sin `loan.parameter.update` o sin servidor: se ve, no se edita. */
    readOnly: boolean;
    /** Parámetro del mock sin llave en loan-config. */
    notConnected: boolean;
    onChange: (id: string, value: ParamValue) => void;
}) {
    const disabled = readOnly || notConnected || !param.editable;
    const isPct = param.kind === 'pct';
    const blankText = param.kind === 'text' && (value as string).trim() === '';
    // pct: guardado como fracción, mostrado como % (paridad con el mock y el servidor).
    const displayValue = isPct ? Math.round((value as number) * 1000) / 10 : (value as number);
    const showDefaultHint = param.category !== 'flags' && value !== param.defaultValue;
    const defaultLabel = isPct
        ? `${String(param.defaultValue)} (${String(Math.round((param.defaultValue as number) * 100))}%)`
        : String(param.defaultValue);

    return (
        <div
            className={cn(
                'grid grid-cols-[1fr_240px_130px] items-center gap-3 border-b border-border/50 px-1.5 py-2 last:border-b-0',
                dirty && 'bg-info/10',
            )}
        >
            <div>
                <div className="text-xs font-semibold">
                    {param.name}
                    {notConnected ? (
                        <span className="ml-2 rounded-[3px] bg-muted px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-muted-foreground">
                            No conectado
                        </span>
                    ) : null}
                </div>
                <div className="mt-0.5 text-[10.5px] leading-snug text-muted-foreground">
                    {param.description}
                </div>
            </div>

            <div className="flex items-center justify-end gap-1.5">
                {param.kind === 'text' ? (
                    // El aviso va fuera del Input: con `error` el kit lo envuelve y el campo pierde el foco.
                    <div className="flex flex-col items-end gap-0.5">
                        <Input
                            value={value as string}
                            disabled={disabled}
                            aria-label={param.name}
                            aria-invalid={blankText ? true : undefined}
                            className={cn(
                                'h-7 w-[230px] text-[11px]',
                                blankText && 'border-destructive',
                            )}
                            onChange={(event) => {
                                onChange(param.id, event.target.value);
                            }}
                        />
                        {blankText ? (
                            <p className="text-[10px] text-destructive">No puede quedar vacía.</p>
                        ) : null}
                    </div>
                ) : param.kind === 'bool' ? (
                    <Switch
                        checked={value as boolean}
                        onCheckedChange={(checked) => {
                            onChange(param.id, checked);
                        }}
                        disabled={disabled}
                        aria-label={param.name}
                    />
                ) : (
                    <NumberInput
                        step={isPct ? 0.5 : 1}
                        value={displayValue}
                        disabled={disabled}
                        aria-label={param.name}
                        className="w-[90px] text-right"
                        onValueChange={(num) => {
                            onChange(param.id, isPct ? num / 100 : num);
                        }}
                    />
                )}
                {param.kind === 'text' ? null : (
                    <span className="min-w-[46px] text-left text-[10.5px] text-muted-foreground">
                        {PARAM_UNIT[param.kind]}
                    </span>
                )}
            </div>

            <div className="text-right text-[10px] text-muted-foreground">
                <div className="font-mono tabular-nums">{param.lastModified}</div>
                <div>{param.lastModifiedBy}</div>
                {showDefaultHint ? (
                    <div className="text-[9.5px] italic text-info">Default: {defaultLabel}</div>
                ) : null}
            </div>
        </div>
    );
}
